import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  EMPTY_TASK_FILTERS,
  PRIORITIES,
  STAGES,
  STAGE_LABELS,
  taskFilterCount,
  type Client,
  type TaskFilters,
  type User,
} from "@board/shared";

export function TaskFilterBar({
  filters,
  clients,
  users,
  focusedClientId,
  visibleCount,
  totalCount,
  onChange,
}: {
  filters: TaskFilters;
  clients: Client[];
  users: User[];
  focusedClientId: string | null;
  visibleCount: number;
  totalCount: number;
  onChange: (filters: TaskFilters) => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const active = taskFilterCount(filters);
  const change = <K extends keyof TaskFilters>(key: K, value: TaskFilters[K]) =>
    onChange({ ...filters, [key]: value });

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", dismiss);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("mousedown", dismiss);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className="railfilter" ref={root}>
      <button
        type="button"
        className={`railfilter__button${active ? " railfilter__button--active" : ""}`}
        aria-label={active ? `Filter stickies, ${active} active` : "Filter stickies"}
        aria-expanded={open}
        title="Filter stickies"
        onClick={() => setOpen((current) => !current)}
      >
        <svg viewBox="0 0 20 20" aria-hidden="true">
          <path d="M3 4.5h14l-5.4 6.1v4.3l-3.2 1.6v-5.9z" />
        </svg>
        {active ? <span className="railfilter__badge">{active}</span> : null}
      </button>

      {open ? (
        <section className="taskfilters" aria-label="Filter stickies">
          <header className="taskfilters__head">
            <strong>Filter stickies</strong>
            <span className="taskfilters__count" aria-live="polite">
              {visibleCount} of {totalCount}
            </span>
            {active ? (
              <button type="button" className="taskfilters__clear" onClick={() => onChange(EMPTY_TASK_FILTERS)}>
                Clear all
              </button>
            ) : null}
          </header>

          <label className="taskfilters__search">
            <span aria-hidden="true">⌕</span>
            <input
              type="search"
              value={filters.query}
              placeholder="Find a sticky"
              aria-label="Search stickies"
              autoFocus
              onChange={(event) => change("query", event.target.value)}
            />
          </label>

          <div className="taskfilters__grid">
            <FilterSelect label="User">
              <select value={filters.userId} onChange={(event) => change("userId", event.target.value)}>
                <option value="all">Everyone</option>
                <option value="me">My tasks</option>
                <option value="unassigned">Unassigned</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </FilterSelect>

            <FilterSelect label="Client">
              <select
                value={focusedClientId ?? filters.clientId}
                disabled={Boolean(focusedClientId)}
                onChange={(event) => change("clientId", event.target.value)}
              >
                <option value="all">All clients</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </FilterSelect>

            <FilterSelect label="Status">
              <select value={filters.stage} onChange={(event) => change("stage", event.target.value as TaskFilters["stage"])}>
                <option value="all">All statuses</option>
                {STAGES.map((stage) => <option key={stage} value={stage}>{STAGE_LABELS[stage]}</option>)}
              </select>
            </FilterSelect>

            <FilterSelect label="Priority">
              <select value={filters.priority} onChange={(event) => change("priority", event.target.value as TaskFilters["priority"])}>
                <option value="all">All priorities</option>
                {PRIORITIES.map((priority) => (
                  <option key={priority} value={priority}>{priority[0]!.toUpperCase() + priority.slice(1)}</option>
                ))}
              </select>
            </FilterSelect>

            <FilterSelect label="Due">
              <select value={filters.due} onChange={(event) => change("due", event.target.value as TaskFilters["due"])}>
                <option value="all">Any date</option>
                <option value="overdue">Overdue</option>
                <option value="due_soon">Due soon</option>
                <option value="upcoming">Upcoming</option>
                <option value="no_date">No due date</option>
              </select>
            </FilterSelect>
          </div>
        </section>
      ) : null}
    </div>
  );
}

function FilterSelect({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="taskfilters__field">
      <span>{label}</span>
      {children}
    </label>
  );
}
